import { create } from 'zustand'
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage'
import { db, storage } from '../lib/firebase'
import { Character, CharacterTrainingRequest } from '../types'
import * as fal from '@fal-ai/serverless-client'

// Initialize fal if API key is present
const falApiKey = import.meta.env.VITE_FAL_API_KEY
if (falApiKey && falApiKey !== 'your_fal_api_key_here') {
  fal.config({
    credentials: falApiKey
  })
}

interface CharacterState {
  characters: Character[]
  loading: boolean
  error: string | null
  trainingInProgress: boolean

  // Character actions
  fetchCharacters: (projectId: string) => Promise<void>
  fetchCharacterById: (characterId: string) => Promise<Character | null>
  createCharacter: (request: CharacterTrainingRequest) => Promise<string>
  trainCharacter: (characterId: string, imagesZipUrl: string) => Promise<void>
  updateCharacter: (id: string, updates: Partial<Character>) => Promise<void>
  deleteCharacter: (id: string) => Promise<void>
  uploadTrainingImages: (files: File[], projectId: string) => Promise<string>

  // Utility
  clearError: () => void
}

export const useCharacterStore = create<CharacterState>((set, get) => ({
  characters: [],
  loading: false,
  error: null,
  trainingInProgress: false,

  fetchCharacters: async (projectId: string) => {
    try {
      set({ loading: true, error: null })
      const q = query(
        collection(db, 'characters'),
        where('projectId', '==', projectId),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)
      const characters = snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate().toISOString(),
          updatedAt: data.updatedAt?.toDate().toISOString(),
        }
      }) as Character[]
      
      set({ characters })
    } catch (error) {
      console.error('Failed to fetch characters:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to fetch characters' })
    } finally {
      set({ loading: false })
    }
  },

  fetchCharacterById: async (characterId: string) => {
    try {
      set({ loading: true, error: null })
      const docRef = doc(db, 'characters', characterId)
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        const data = docSnap.data()
        const character = {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate().toISOString(),
          updatedAt: data.updatedAt?.toDate().toISOString(),
        } as Character
        
        // Update local state
        set(state => ({
          characters: state.characters.some(c => c.id === characterId)
            ? state.characters.map(c => c.id === characterId ? character : c)
            : [...state.characters, character]
        }))
        
        return character
      }
      
      return null
    } catch (error) {
      console.error('Failed to fetch character:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to fetch character' })
      return null
    } finally {
      set({ loading: false })
    }
  },

  createCharacter: async (request: CharacterTrainingRequest) => {
    try {
      set({ loading: true, error: null })
      
      // Create character document with pending status
      const characterData = {
        projectId: request.projectId,
        ownerId: request.ownerId,
        name: request.name,
        triggerWord: request.triggerWord,
        description: request.description || '',
        loraWeightsUrl: '', // Will be filled after training
        status: 'pending',
        trainingSteps: request.trainingSteps,
        isStyle: request.isStyle,
        trainingImagesCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      
      const docRef = await addDoc(collection(db, 'characters'), characterData)
      
      // Start training
      await get().trainCharacter(docRef.id, request.imagesZipUrl)
      
      // Refresh characters
      await get().fetchCharacters(request.projectId)
      
      return docRef.id
    } catch (error) {
      console.error('Failed to create character:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to create character' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  trainCharacter: async (characterId: string, imagesZipUrl: string) => {
    try {
      set({ trainingInProgress: true, error: null })
      
      // Get character data
      const character = get().characters.find(c => c.id === characterId) 
        || await get().fetchCharacterById(characterId)
      
      if (!character) {
        throw new Error('Character not found')
      }

      // Update status to training
      await updateDoc(doc(db, 'characters', characterId), {
        status: 'training',
        trainingProgress: 0,
        updatedAt: serverTimestamp(),
      })

      // Update local state
      set(state => ({
        characters: state.characters.map(c => 
          c.id === characterId 
            ? { ...c, status: 'training' as const, trainingProgress: 0 }
            : c
        )
      }))

      console.log('Starting LoRA training for character:', character.name)
      console.log('Images ZIP URL:', imagesZipUrl)
      console.log('Trigger word:', character.triggerWord)
      console.log('Steps:', character.trainingSteps)

      // Call fal.ai flux-lora-fast-training API
      const result: any = await fal.subscribe('fal-ai/flux-lora-fast-training', {
        input: {
          images_data_url: imagesZipUrl,
          trigger_word: character.triggerWord,
          steps: character.trainingSteps,
          is_style: character.isStyle,
          create_masks: true,
          is_input_format_already_preprocessed: false,
        },
        logs: true,
        onQueueUpdate: (update) => {
          console.log('Training queue update:', update)
          if (update.status === 'IN_PROGRESS') {
            // Update progress in local state
            set(state => ({
              characters: state.characters.map(c => 
                c.id === characterId 
                  ? { ...c, trainingProgress: 50 }
                  : c
              )
            }))
          }
        },
      })

      console.log('Training result:', result)

      // Extract weights URL from result
      const loraWeightsUrl = result.diffusers_lora_file?.url || result.config_file?.url || ''
      const configUrl = result.config_file?.url || ''

      if (!loraWeightsUrl) {
        throw new Error('Training completed but no weights URL returned')
      }

      // Update character with training results
      await updateDoc(doc(db, 'characters', characterId), {
        status: 'ready',
        loraWeightsUrl: loraWeightsUrl,
        configUrl: configUrl,
        trainingProgress: 100,
        updatedAt: serverTimestamp(),
      })

      // Update local state
      set(state => ({
        characters: state.characters.map(c => 
          c.id === characterId 
            ? { 
                ...c, 
                status: 'ready' as const, 
                loraWeightsUrl,
                configUrl,
                trainingProgress: 100 
              }
            : c
        )
      }))

      console.log('Character training completed:', character.name)
      console.log('LoRA weights URL:', loraWeightsUrl)

    } catch (error) {
      console.error('Training failed:', error)
      
      // Update character with error status
      await updateDoc(doc(db, 'characters', characterId), {
        status: 'failed',
        trainingError: error instanceof Error ? error.message : 'Training failed',
        updatedAt: serverTimestamp(),
      })

      // Update local state
      set(state => ({
        characters: state.characters.map(c => 
          c.id === characterId 
            ? { 
                ...c, 
                status: 'failed' as const, 
                trainingError: error instanceof Error ? error.message : 'Training failed'
              }
            : c
        ),
        error: error instanceof Error ? error.message : 'Training failed'
      }))

      throw error
    } finally {
      set({ trainingInProgress: false })
    }
  },

  updateCharacter: async (id, updates) => {
    try {
      set({ loading: true, error: null })
      await updateDoc(doc(db, 'characters', id), {
        ...updates,
        updatedAt: serverTimestamp(),
      })
      
      set(state => ({
        characters: state.characters.map(c => 
          c.id === id ? { ...c, ...updates } : c
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update character' })
    } finally {
      set({ loading: false })
    }
  },

  deleteCharacter: async (id) => {
    try {
      set({ loading: true, error: null })
      await deleteDoc(doc(db, 'characters', id))
      
      set(state => ({
        characters: state.characters.filter(c => c.id !== id)
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete character' })
    } finally {
      set({ loading: false })
    }
  },

  uploadTrainingImages: async (files: File[], projectId: string) => {
    try {
      set({ loading: true, error: null })
      
      // Create a ZIP file from the uploaded images
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()
      
      // Add each file to the ZIP
      for (const file of files) {
        const arrayBuffer = await file.arrayBuffer()
        zip.file(file.name, arrayBuffer)
      }
      
      // Generate the ZIP blob
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      
      // Upload ZIP to Firebase Storage
      const timestamp = Date.now()
      const storageRef = ref(storage, `projects/${projectId}/training/training_images_${timestamp}.zip`)
      const snapshot = await uploadBytes(storageRef, zipBlob, {
        contentType: 'application/zip',
      })
      
      const downloadUrl = await getDownloadURL(snapshot.ref)
      console.log('Training images ZIP uploaded:', downloadUrl)
      
      return downloadUrl
    } catch (error) {
      console.error('Failed to upload training images:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to upload training images' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  clearError: () => set({ error: null }),
}))

